import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import { Box, FormControl, MenuItem, Select, Button, Snackbar, Typography, LinearProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons';
import apiService from '../../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import DeleteTeam from '../../components/Settings/DeleteTeam'; // Assuming this is the modal component

const downloadIcon = apiService.getBlobAssetUrl('download.svg');
const search = apiService.getBlobAssetUrl('search.svg');
const closeimage = apiService.getBlobAssetUrl('close_12.svg');
const deleteteams = apiService.getBlobAssetUrl('DeleteTeams_12.svg'); // Added delete icon
const accessToken = Cookies.get('access_token');
const application_url = window._env_?.REACT_APP_APPLICATION_URL;

const OrgMembersTab = ({ onSave }) => {
  const [email, setEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [searchbarTerm, setSearchbarTerm] = useState('');
  const [members, setMembers] = useState([]);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [settings, setSettings] = useState([]);
  const [role, setRole] = useState([]);
  const [users, setUsers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [sortConfig] = useState({ key: null, direction: 'asc' });
  const [settingToDelete, setSettingToDelete] = useState(null); // Track member to delete
  const [showDeleteModal, setShowDeleteModal] = useState(false); // Control delete modal

  // Fetch members data
  const loadMembersData = async () => {
    setLoading(true);
    try {
      const data = await apiService.fetchData('role_assignment');
      const results = data?.results || [];
      const formatted = results.map((item) => ({
        name: item.Name || item.name || 'Unknown',
        iD: item.ID || item.id,
        email: item.EmailID || item.email || item.emailId || item.Email || '',
        role: item.Role || item.role || item.RoleName || item.rolename || '',
        createdBy: item.CreatedBy || item.createdBy || 'Unknown',
        permission: item.Permission || item.permission || '',
      }));
      console.log('Formatted Members:', formatted);
      setMembers([...formatted]);
      setSettings(formatted);
    } catch (error) {
      console.error('Error fetching org members:', error);
      setSnackbarMessage('Failed to load members.');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Fetch roles
  const loadRoles = async () => {
    setLoading(true);
    try {
      const data = await apiService.fetchData('roleslist');
      const results = data?.results || [];
      const formatted = results.map((item) => ({
        role: item.Role || item.role,
        iD: item.ID || item.id,
      }));
      setRole(formatted);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setSnackbarMessage('Failed to load roles.');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Fetch users for suggestions
  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const data = await apiService.fetchData('userslist');
        const results = data?.results || [];
        const formatted = results.map((item) => ({
          ID: item.ID,
          Name: item.Name,
          EmailID: item.EmailID,
        }));
        setUsers(formatted);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    loadUserInfo();
  }, []);

  // Load roles and members on mount
  useEffect(() => {
    const fetchData = async () => {
      await loadRoles();
      await loadMembersData();
    };
    fetchData();
  }, []);

  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 when search term changes
  }, [searchbarTerm]);

  // Handle email input for invite
  const handleEmailInputChange = (e) => {
    const input = e.target.value;
    setEmail(input);

    if (input.trim() === '') {
      setSuggestions([]);
      return;
    }

    const filteredSuggestions = users.filter(
      (user) =>
        (user?.EmailID?.toLowerCase().includes(input.toLowerCase())) ||
        (user?.Name?.toLowerCase().includes(input.toLowerCase()))
    );
    setSuggestions(filteredSuggestions);
  };

  // Handle role change for existing members
  const handleRoleChange = useCallback(
    async (userId, selectedRoleId) => {
      if (!userId || !selectedRoleId) {
        setSnackbarMessage('Missing user ID or role ID.');
        setSnackbarOpen(true);
        return;
      }
      const user_id = sessionStorage.getItem('user_id');
      const payload = {
        ID: userId,
        RoleID: selectedRoleId,
        ModifiedBy: user_id,
        target_user_ids: [],
        created_by: user_id,
      };

      try {
        await apiService.putData('role_assignment', payload, accessToken);
        setSnackbarMessage('Permissions successfully updated!');
        setSnackbarOpen(true);
        await loadMembersData();
        if (typeof onSave === 'function') {
          onSave({ iD: userId, role: selectedRoleId });
        }
      } catch (error) {
        console.error('Failed to update permissions:', error);
        setSnackbarMessage(`Failed to update permissions: ${error.response?.data?.message || error.message}`);
        setSnackbarOpen(true);
      }
    },
    [accessToken, onSave]
  );

  // Clear email input
  const handleClearInput = () => {
    setEmail('');
    setSuggestions([]);
  };

  // Handle suggestion click
  const handleSuggestionClick = (user) => {
    setEmail(user.EmailID);
    setSuggestions([]);
  };

  // Filter and sort members
  const filteredMembers = useMemo(() => {
    let filtered = members.filter(
      (member) =>
        (member.name || '').toLowerCase().includes(searchbarTerm.toLowerCase()) ||
        (member.email || '').toLowerCase().includes(searchbarTerm.toLowerCase()) ||
        (member.role || '').toLowerCase().includes(searchbarTerm.toLowerCase())
    );

    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        const aVal = a[sortConfig.key] || '';
        const bVal = b[sortConfig.key] || '';
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [members, searchbarTerm, sortConfig]);

  // Pagination logic
  const totalRows = filteredMembers.length;
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredMembers.slice(indexOfFirstRow, indexOfLastRow);

  const maxVisiblePages = 3;

  const getPageRange = () => {
    const totalPages = Math.ceil(totalRows / rowsPerPage);
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    return { startPage, endPage, totalPages };
  };

  const { startPage, endPage, totalPages } = getPageRange();

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Handle invite
  const handleInvite = async () => {
    if (!email.trim() || !selectedRole) {
      setSnackbarMessage('Please enter an email and select a role.');
      setSnackbarOpen(true);
      return;
    }

    const nameFromEmail = email.trim().split('@')[0];
    const roleId = selectedRole.iD || selectedRole;
    if (typeof roleId !== 'number') {
      setSnackbarMessage('Invalid role selected. Please select a valid role.');
      setSnackbarOpen(true);
      return;
    }

    const userId = sessionStorage.getItem('user_id');

    try {
      const userData = await apiService.fetchData('userslist');
      const existingUser = userData?.results?.find((user) => user.EmailID === email);

      if (existingUser) {
        const roleCheckPayload = {
          target_user_ids: [existingUser.ID],
          RoleID: roleId,
          created_by: userId,
        };

        const roleCheckResponse = await apiService.postData('role_assignment', roleCheckPayload, accessToken);

        if (
          roleCheckResponse.already_assigned_users?.includes(existingUser.ID) ||
          roleCheckResponse.cannot_assign_users?.includes(existingUser.ID)
        ) {
          setSnackbarMessage('This user already has a role assigned.');
          setSnackbarOpen(true);
          return;
        }

        if (roleCheckResponse.already_invited_users?.includes(existingUser.ID)) {
          setSnackbarMessage('This user has already been invited.');
          setSnackbarOpen(true);
          return;
        }
      }

      const emailPayload = {
        endpoint: 'send_email',
        customerName: nameFromEmail,
        surveyLink: '',
        surveyType: '',
        projectName: '',
        send_to: email,
        subject: 'Invitation to CSAT',
        token: accessToken,
        templateType: 'OrgInvitation',
        RoleName: selectedRole.role,
        appLink: application_url,
        teamName: '',
      };

      const emailResponse = await apiService.sendEmail(
        emailPayload.endpoint,
        emailPayload.customerName,
        emailPayload.surveyLink,
        emailPayload.surveyType,
        emailPayload.projectName,
        emailPayload.send_to,
        emailPayload.subject,
        emailPayload.token,
        emailPayload.templateType,
        emailPayload.RoleName,
        emailPayload.appLink,
        emailPayload.teamName
      );

      let newUserId = null;
      if (emailResponse?.userId) {
        newUserId = emailResponse.userId;
      } else {
        const userData = await apiService.fetchData('userslist');
        const newUser = userData?.results?.find((user) => user.EmailID === email);
        if (newUser) {
          newUserId = newUser.ID;
        } else {
          throw new Error('New user not found after sending invitation.');
        }
      }

      const inviteData = {
        RoleID: roleId,
        target_user_ids: [newUserId],
        created_by: userId,
      };
      const response = await apiService.postData('role_assignment', inviteData, accessToken);
      console.log('User added success:', response);

      await loadMembersData();

      setSnackbarMessage('Invitation sent successfully!');
      setSnackbarOpen(true);
      setEmail('');
      setSelectedRole('');
      setSearchbarTerm('');
    } catch (error) {
      console.error('Invite Error:', error.response?.data || error);
      const message = error.response?.data?.message || error.message || 'Unknown error occurred.';
      setSnackbarMessage(`Invite Failed: ${message}`);
      setSnackbarOpen(true);
    }
  };

  const toggleDownloadMenu = () => setShowDownloadMenu(!showDownloadMenu);

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text('Org-Members Table', 10, 10);
    autoTable(doc, {
      startY: 20,
      head: [['ID', 'EmailID', 'Name', 'Role']],
      body: settings.map((row) => [
        row.iD,
        row.email,
        row.name,
        row.role,
      ]),
    });
    doc.save('org-members.pdf');
  };

  const exportToCSV = () => {
    const headers = ['ID', 'EmailID', 'Name', 'Role'].join(',');
    const rows = settings.map((row) =>
      [
        row.iD,
        row.email,
        row.name,
        row.role,
      ]
        .map((field) => `"${field}"`)
        .join(',')
    );
    const csvContent = `data:text/csv;charset=utf-8,${headers}\n${rows.join('\n')}`;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'org-members_data.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderSuggestionText = (text, searchTerm) => {
    if (!searchTerm) return <span>{text}</span>;

    const lowerText = text.toLowerCase();
    const lowerSearchTerm = searchTerm.toLowerCase();
    const index = lowerText.indexOf(lowerSearchTerm);

    if (index === -1) return <span>{text}</span>;

    const beforeMatch = text.substring(0, index);
    const match = text.substring(index, index + searchTerm.length);
    const afterMatch = text.substring(index + searchTerm.length);

    return (
      <>
        <span className="suggestion-non-match">{beforeMatch}</span>
        <span className="suggestion-match">{match}</span>
        <span className="suggestion-non-match">{afterMatch}</span>
      </>
    );
  };

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    if (!settingToDelete) return;

    try {
      await apiService.deleteData('role_assignment', { ID: settingToDelete.iD }, accessToken);
      setSnackbarMessage('User removed from the organization successfully.');
      setSnackbarOpen(true);
      setShowDeleteModal(false);
      await loadMembersData();
    } catch (error) {
      console.error('Failed to remove user from organization:', error);
      setSnackbarMessage(`Failed to remove user: ${error.response?.data?.message || error.message}`);
      setSnackbarOpen(true);
      setShowDeleteModal(false);
    }
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSettingToDelete(null);
  };

  // Handle delete click
  const handleDeleteClick = (row) => {
    setSettingToDelete(row);
    setShowDeleteModal(true);
  };

  if (loading) {
    return (
      <Box id="loading1">
        <Box id="loading2" sx={{ mb: 2 }}>
          <LinearProgress id="loading3" />
        </Box>
        <Typography id="loading4" variant="h6">
          Loading Org Members Data...
        </Typography>
      </Box>
    );
  }

  return (
    <div className="org-members-tab1">
      <h2 id="settings1">Organization Members</h2>
      <br />

      {/* Invite Section */}
      <div className="invite-container1">
        <div className="email-input-wrapper">
          <input
            title="Type CriticalRiver email ID"
            type="email"
            placeholder="Type CriticalRiver email ID"
            value={email}
            onChange={handleEmailInputChange}
            className="email-input1"
          />
          {email && (
            <button
              type="button"
              className="clear-img11"
              onClick={handleClearInput}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleClearInput();
                }
              }}
              aria-label="Clear email input"
              tabIndex={0}
            >
              <img src={closeimage} alt="clear" />
            </button>
          )}
          {suggestions.length > 0 && (
            <ul className="suggestions-dropdown">
              {suggestions.map((user, index) => (
                <li
                  key={user.ID}
                  onClick={() => handleSuggestionClick(user)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleSuggestionClick(user);
                    }
                  }}
                  className={`suggestion-item ${index % 2 === 0 ? 'even' : 'odd'}`}
                  tabIndex={0}
                >
                  <div>{renderSuggestionText(user.Name, email)}</div>
                  <div>{renderSuggestionText(user.EmailID, email)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <FormControl className="role-select1">
<Select
    title="Select Role"
    value={selectedRole ? selectedRole.iD : ''}
    onChange={(e) => {
      const selectedRoleId = e.target.value;
      const selected = role.find((r) => r.iD === selectedRoleId) || '';
      setSelectedRole(selected);
    }}
    displayEmpty
    placeholder="Select Role"
>
<MenuItem value="">Select Role</MenuItem>
    {role.map((r) => (
<MenuItem key={r.iD} value={r.iD}>
        {r.role}
</MenuItem>
    ))}
</Select>
        </FormControl>
        <Button
          title="Send Invite"
          type="button"
          className="invite-button"
          id="invite-button11"
          onClick={handleInvite}
        >
          Send Invite
        </Button>
      </div>

      {/* Search */}
      <div className="team-name-bar" id="team-name-bar1">
        <div className="search-container2">
          <input
            title="Search Org Member"
            type="search"
            className="search-input2"
            placeholder="Search Org Member"
            value={searchbarTerm}
            onChange={(e) => setSearchbarTerm(e.target.value)}
          />
          <img src={search} alt="Search" className="search-img1" />
        </div>

        {/* Pagination Info */}
        <div id="results5">
          <div className="settings-download-wrapper">
            <button
              className="download-icon-button"
              onClick={toggleDownloadMenu}
              aria-label="Toggle download options"
            >
              <img src={downloadIcon} alt="Download" className="settings-download-icon" />
            </button>
            {showDownloadMenu && (
              <div className="Project-export-dropdown">
                <button
                  id="Project-export-option11"
                  className="Project-export-option"
                  onClick={exportToCSV}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') exportToCSV(); }}
                  aria-label="Download CSV file"
                >
                  Download CSV
                </button>
                <button
                  id="Project-export-option11"
                  className="Project-export-option"
                  onClick={exportPDF}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') exportPDF(); }}
                  aria-label="Download PDF file"
                >
                  Download PDF
                </button>
              </div>
            )}
          </div>
          <Box>
            <Typography id="results">
              Results: {totalRows === 0 ? 0 : indexOfFirstRow + 1}-{Math.min(indexOfLastRow, totalRows)} of {totalRows}
            </Typography>
          </Box>
          <select
            className="items-per-page-select"
            id="items-per-page-select1"
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(parseInt(e.target.value));
              setCurrentPage(1);
            }}
          >
            {[5, 10, 15, 20].map((count) => (
              <option key={count} value={count}>
                {count}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Member List */}
      <div className="member-list1">
        {currentRows.length === 0 ? (
          <p id="members1" title="No members found">No members found</p>
        ) : (
          currentRows.map((member) => (
            <div className="member-item1" key={member.iD || member.email}>
              <div className="member-info1">
                <p className="member-name1" title={member.name}>{member.name}</p>
                <p className="member-email1" title={member.email}>{member.email}</p>
              </div>
              <FormControl className="member-role-select1">
                <Select
                  title={member.role}
                  value={member.role}
                  onChange={(e) => {
                    const updatedRoleId = e.target.value;
                    handleRoleChange(member.iD, updatedRoleId);
                  }}
                  displayEmpty
                  renderValue={(selected) => {
                    const selectedRole = role.find((r) => r.iD === selected);
                    return selectedRole ? selectedRole.role : member.role;
                  }}
                >
                  <MenuItem value="" id="selected">Select Role</MenuItem>
                  {role.map((r) => (
                    <MenuItem key={r.iD} value={r.iD}>
                      {r.role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
                            <button
                type="button"
                id="delete-img21"
                className="text-red-600 hover:text-red-800"
                onClick={() => handleDeleteClick(member)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleDeleteClick(member);
                  }
                }}
                aria-label={`Remove ${member.name} from organization`}
                tabIndex={0}
              >
                <img src={deleteteams} alt="delete icon" className="delete-img1" title="Delete" />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Pagination Controls */}
      <div className="Settings-table-footer">
        <div className="Settings-pagination">
          <button
            onClick={handlePrevious}
            disabled={currentPage === 1}
            className="Settings-pagination-button"
          >
            <FontAwesomeIcon icon={faAngleLeft} />
          </button>
          {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
            const pageNumber = startPage + i;
            const isSelected = currentPage === pageNumber;
            const buttonClass = `Settings-pagination-button ${isSelected ? 'Settings-selected' : ''}`;
            return (
              <button
                key={pageNumber}
                onClick={() => paginate(pageNumber)}
                className={buttonClass}
              >
                {pageNumber}
              </button>
            );
          })}
          <button
            onClick={handleNext}
            disabled={currentPage === totalPages}
            className="Settings-pagination-button"
          >
            <FontAwesomeIcon icon={faAngleRight} />
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <DeleteTeam
            settingName={settingToDelete?.name || 'Unknown'}
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
            setSnackbarOpen={setSnackbarOpen}
            setSnackbarMessage={setSnackbarMessage}
          />
        </div>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: snackbarMessage.includes('successful') ? '#4caf50' : '#f44336',
          },
        }}
      />
    </div>
  );
};

OrgMembersTab.propTypes = {
  onSave: PropTypes.func.isRequired,
};

export default OrgMembersTab;